import { AML_RISK_LEVELS } from "./amlRiskLevel";
import { screenCountryRisk } from "./countryRiskRating";
import { resolveClientDueDiligenceCountries } from "./clientDueDiligenceValues";
import { formatOccupationLabel } from "./occupation";
import {
  formatScreenedAt,
  nameScreeningRowsFromRecord,
  screenRecordStatusText,
} from "./nameScreening";
import { screenRelatedParty } from "./screenRelatedParty";
import { addressTypeLabel } from "./addressTypes";

export const FWD_AML_ASSESSMENT_VERSION = "fwd-2026-07";

export const DASH = "—";

export const ASSESSEE_TYPES = [
  { value: "individual", label: "Individual Client" },
  { value: "corporate", label: "Corporate Client" },
  { value: "advisor", label: "Advisor" },
  { value: "staff", label: "Staff" },
];

export const REVIEW_TYPES = [
  { value: "new", label: "New Business / Transaction" },
  { value: "periodic", label: "Periodic" },
  { value: "trigger", label: "Trigger" },
];

export const CLIENT_STATUSES = [
  { value: "existing", label: "Existing client" },
  { value: "new", label: "New client" },
];

export const COUNTRY_RISK_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "targeted", label: "Targeted/Sectoral sanctioned" },
  { value: "comprehensive", label: "Comprehensive sanctioned" },
];

export const PREVIOUS_RISK_OPTIONS = [
  { value: "N/A", label: "N/A" },
  { value: "Exempted Risk", label: "Exempted" },
  { value: "Low Risk", label: "Low" },
  { value: "Standard Risk", label: "Standard" },
  { value: "Increased Risk", label: "Increased" },
  { value: "Unacceptable Risk", label: "Unacceptable" },
];

export const SCREENED_PARTIES = [
  { id: "policyOwner", label: "Policy owner / Account owner" },
  { id: "insured", label: "Insured" },
  { id: "beneficiary", label: "Beneficiary" },
  { id: "director", label: "Director" },
  { id: "shareholder", label: "Shareholder" },
  { id: "others", label: "Others (please specify)", detail: true },
];

export const TRUE_HIT_TYPES = [
  { id: "terrorist", label: "Terrorist", effect: "unacceptable" },
  {
    id: "sanctioned",
    label: "Sanctioned person and/or Individuals/ Entities on OFAC's sanction list",
    effect: "unacceptable",
  },
  { id: "coldShoulder", label: "Cold shoulder order by SFC", effect: "unacceptable" },
  {
    id: "pep",
    label: "Hong Kong/ Non- Hong Kong / International organization PEP, RCA of a PEP",
    effect: "increased",
  },
  {
    id: "connectedParty",
    label:
      "Connected Party including ultimate beneficial owner or beneficiary of a PEP-owned or controlled entity",
    effect: "increased",
  },
  {
    id: "appointedPep",
    label:
      "A natural person appointed to act on behalf of the customer is a PEP or subsequently becomes a PEP",
    effect: "increased",
  },
  { id: "adverseNews", label: "Associated with Material Adverse News", effect: "increased" },
  {
    id: "sanctionAssociate",
    label: "An associate with a sanctioned person or entity",
    effect: "increased",
  },
  { id: "soe", label: "State-owned Entities", effect: "continue" },
];

export const UNACCEPTABLE_FACTORS = [
  { id: "anonymous", label: "Anonymous customers" },
  { id: "fictitious", label: "Customer in obviously fictitious names or other personal details" },
  {
    id: "shellBanks",
    label:
      "Shell banks, entities or natural persons who deal with or provide services to shell banks, unregulated/unlicensed banks, unregulated/unlicensed money lender/changers or money remittance businesses, unregulated/unlicensed virtual currencies issuers or intermediaries",
  },
  { id: "frontCompanies", label: "Front companies or natural persons who own the front companies" },
  {
    id: "shellCompanies",
    label:
      "Shell companies or natural persons who own the shell companies without legitimate reason/s for the establishment of that shell company",
  },
  { id: "casinoJunket", label: "Licensed or unlicensed casino junket" },
  { id: "unlicensedCasino", label: "Unlicensed casino/gambling business" },
  {
    id: "sanctionedParties",
    label: "Sanctioned persons/entities and parties involved in sanctioned activities",
  },
  { id: "internalBlacklist", label: "Prohibited under FWD Internal Blacklist" },
  {
    id: "bearerShares",
    label: "(Corporate Client only) Companies that issue with bearer shares",
    corporateOnly: true,
  },
  {
    id: "unregisteredCharities",
    label: "(Corporate Client only) Unregulated or unregistered charities",
    corporateOnly: true,
  },
];

export const BUSINESS_TYPES = [
  { id: "accountOpening", label: "Account Opening Only" },
  { id: "life", label: "Life Insurance (New business & Transfer-in)" },
  { id: "investment", label: "Investment Business (New business & Transfer-in)" },
  { id: "others", label: "Others (please specify)", detail: true },
  { id: "fundSwitching", label: "Fund / Bond Switching" },
  { id: "generalInsurance", label: "General Insurance" },
  { id: "mpf", label: "MPF Business" },
];

export const FACTORS_15A = [
  {
    id: "str",
    label: "Suspicious transaction report has been filed on the customer or the connected party",
  },
  {
    id: "highRiskBusiness",
    label: "Owns or operates high risk business/industries (Refer to Annex 1)",
  },
  {
    id: "sanctionedCountry",
    label:
      "Residential and/ or correspondence address is associated with any targeted/sectoral sanctioned country",
  },
];

export const FACTORS_15B = [
  {
    id: "professionalInvestor",
    label: "Is the assessee classified as 'Professional Investor' and holding investment product in FP",
  },
  {
    id: "nonResident",
    label:
      "Non-resident customers who have no discernible reasons for opening an investment account with FWDFP in Hong Kong",
  },
  {
    id: "assetHolding",
    label:
      "The use of legal persons or arrangements as personal asset-holding vehicles without any commercial or other valid reasons",
  },
  {
    id: "overseasIntroducer",
    label:
      "Customer is introduced by an overseas financial institution, affiliate or other investor, both of which are based in jurisdictions posing a higher risk",
  },
  {
    id: "nominee",
    label:
      "(Apply to corporate client only) Companies that have nominee shareholders, nominee directors, bearer shares or bearer share warrants",
    na: true,
    corporateOnly: true,
  },
  {
    id: "complexOwnership",
    label:
      "(Apply to corporate client only) The ownership structure of a company appears unusual or excessively complex having considered the nature of the company's business",
    na: true,
    corporateOnly: true,
  },
];

export const MONETARY_ROWS = [
  {
    id: "life",
    label:
      "For Life insurance business only. Is the regular/ single premium (including top ups and new policies) ≥ HKD 7,800,000?",
    na: true,
  },
  {
    id: "lifeAndInvestment",
    label:
      "For Life insurance business + Investment business. Is the regular/ single premium (including top ups and new policies) ≥ HKD 10,800,000?",
    na: true,
  },
];

export const DUAL_FACTORS = [
  {
    id: "q17",
    number: "17",
    label:
      "Occupation and business sector. Being UBO from High-Risk Occupation / Industries/ Business (Refer to Annex 1)?",
  },
  {
    id: "q18",
    number: "18",
    label:
      "Country. Is the residential/correspondence address associated with any high-risk country/jurisdiction?",
  },
  {
    id: "q19Life",
    number: "19",
    label:
      "For Life insurance business. Is the insurance policy he/she owned and going to apply belonging to ILAS, universal life or policy with premium financing over 80% of total cash value?",
    na: true,
  },
  {
    id: "q19Investment",
    number: "19",
    label:
      "For Investment business. Is the deposit made for a sole iFAST account from jointly-owned bank account?",
    na: true,
  },
  {
    id: "q20NonFace",
    number: "20",
    label:
      "Distribution channel. Is it a non-face-to-face (including virtual onboarding and phone application) application?",
  },
  {
    id: "q20LifePayment",
    number: "20",
    label:
      "Life insurance business. Payments via intermediary that may obscure the source of payment (e.g., long chain of intermediaries).",
    na: true,
  },
  {
    id: "q20Investment",
    number: "20",
    label:
      "For Investment business. Is the assessee rely on third parties to conduct CDD or use complexity of transaction chain, such as layers of distribution or sub-distribution?",
    na: true,
  },
  {
    id: "q21",
    number: "21",
    label:
      "Legal structure (corporate client only). Is the customer in the form of a trust, foundation, a personal investment company with nominee shareholders?",
  },
];

export const ANNEX_1 = [
  "Armaments and weapons related industries, including manufacturers, dealers and intermediaries.",
  "Licensed Casinos, betting and other gambling related businesses.",
  "Licensed Money Service Businesses (MSBs), including money changers, remittance agents and third-party payment platforms.",
  "Licensed Virtual Asset/Currency Service Providers.",
  "Embassies and foreign consulates.",
  "Extractive industries such as quarrying, mining or logging.",
  "Dealers in high value or precious goods, including jewellers, art and antique dealers.",
  "Cash intensive businesses such as pawn brokers, bars, nightclubs and convenience stores.",
  "Registered charitable and non-profit organizations/foundations.",
];

export const PAPER_NOTES = {
  note1:
    "Approval from Head of FP (or delegate) is required when there is risk rating adjustment (including lowering or uplifting the risk rating) based on risk factor(s) identified in this form.",
  note2:
    "For exceptional circumstances where FWDFP decides that the final risk rating of a customer must be manually adjusted, rationale should be properly documented and retained; and appropriate approval should be sought via MLRO.",
};

const UNACCEPTABLE_HIT_IDS = new Set(
  TRUE_HIT_TYPES.filter((hit) => hit.effect === "unacceptable").map((hit) => hit.id)
);

const text = (value) => String(value ?? "").trim();

const filled = (value) => text(value) || DASH;

const clientTypeLabel = (type) => {
  const labels = {
    INDIVIDUAL: "Individual",
    CORPORATE: "Corporate",
    STAFF: "Staff",
    CONSULTANT: "Consultant",
  };
  return labels[text(type).toUpperCase()] || "";
};

export const mapAssesseeType = (type) => {
  const normalized = text(type).toUpperCase();
  if (normalized === "CORPORATE") return "corporate";
  if (normalized === "STAFF") return "staff";
  if (normalized === "CONSULTANT") return "advisor";
  if (normalized === "INDIVIDUAL") return "individual";
  return "";
};

const personName = (person) => {
  if (!person || typeof person !== "object") return "";
  return (
    text(person.name) ||
    text(person.displayName) ||
    text(person.aliasName) ||
    text(person.username) ||
    [text(person.surname), text(person.givenName)].filter(Boolean).join(" ") ||
    text(person.staffId)
  );
};

export const clientDisplayName = (client = {}) => {
  if (text(client.type).toUpperCase() === "CORPORATE") {
    return [text(client.name), text(client.cname)].filter(Boolean).join(" · ");
  }
  const english = [text(client.surname), text(client.givenName)].filter(Boolean).join(" ");
  return [english || text(client.name), text(client.cname)].filter(Boolean).join(" · ");
};

const consultantLabel = (client = {}) =>
  (Array.isArray(client.consultants) ? client.consultants : [])
    .map(personName)
    .filter(Boolean)
    .join(", ");

const formatDay = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return value.trim().slice(0, 10);
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const yesCount = (answers, rows) =>
  rows.filter((row) => answers?.[row.id] === "yes").length;

const selectedTypes = (assessment) => new Set(assessment?.businessTypes || []);

export function assessmentRoute(assessment = {}) {
  const comprehensive = assessment.highestCountryRisk === "comprehensive";
  const unacceptableHit =
    assessment.trueHit === "yes" &&
    (assessment.trueHitTypes || []).some((id) => UNACCEPTABLE_HIT_IDS.has(id));
  const question12Yes = Object.values(assessment.unacceptableFactors || {}).includes("yes");
  const types = selectedTypes(assessment);
  const onlyExempted =
    types.size > 0 &&
    [...types].every((id) => id === "generalInsurance" || id === "mpf");
  const onlyFund = types.size > 0 && [...types].every((id) => id === "fundSwitching");

  let hardStop = null;
  let reason = "";
  if (comprehensive || unacceptableHit || question12Yes) {
    hardStop = "unacceptable";
    if (comprehensive) {
      reason = "Comprehensive sanctioned routes to Section 6 as Unacceptable Risk.";
    } else if (unacceptableHit) {
      reason = "An unacceptable true hit routes to Section 6 as Unacceptable Risk.";
    } else {
      reason = "A Yes in question 12 routes to Section 6 as Unacceptable Risk.";
    }
  } else if (onlyExempted) {
    hardStop = "exempted";
    reason = "General Insurance or MPF only routes to Section 6 as Exempted Risk.";
  } else if (onlyFund) {
    reason = "Fund / bond switching routes to Section 6.";
  }

  const life = types.has("life");
  const investment = types.has("investment");
  const accountOrOther = types.has("accountOpening") || types.has("others");
  const blockedAt2 = comprehensive;
  const blockedAt3 = comprehensive || unacceptableHit;
  const blockedAt4 = blockedAt3 || question12Yes;
  const show15a = !blockedAt4 && (life || investment || accountOrOther);
  const show15b = !blockedAt4 && investment;
  const show16 = !blockedAt4 && (life || investment);
  const showSection5 = !blockedAt4 && (life || investment);
  const factorsAnswered = UNACCEPTABLE_FACTORS.every((factor) => {
    const value = assessment.unacceptableFactors?.[factor.id];
    return value === "no" || value === "na";
  });

  return {
    hardStop,
    reason,
    skipToSection6: Boolean(hardStop) || onlyFund,
    showQuestion11: !blockedAt2 && assessment.trueHit === "yes",
    show15a,
    show15b,
    show16,
    showSection5,
    question13: question12Yes ? "yes" : factorsAnswered ? "no" : "",
    sectionOpen: {
      1: true,
      2: !blockedAt2,
      3: !blockedAt3,
      4: !blockedAt4,
      5: showSection5,
      6: true,
    },
  };
}

export function riskFactorCounts(assessment = {}) {
  const route = assessmentRoute(assessment);
  const singleRows = [
    ...(route.show15a ? FACTORS_15A : []),
    ...(route.show15b ? FACTORS_15B : []),
    ...(route.show16 ? MONETARY_ROWS : []),
  ];
  const singleAnswers = {
    ...(assessment.factors15a || {}),
    ...(assessment.factors15b || {}),
    ...(assessment.monetary || {}),
  };
  return {
    single: yesCount(singleAnswers, singleRows),
    dual: route.showSection5 ? yesCount(assessment.dualFactors, DUAL_FACTORS) : 0,
  };
}

export function suggestFwdAmlRisk(assessment = {}) {
  const route = assessmentRoute(assessment);
  if (route.hardStop === "unacceptable") return "Unacceptable Risk";
  if (route.hardStop === "exempted") return "Exempted Risk";

  const counts = riskFactorCounts(assessment);
  if (counts.single >= 1 || counts.dual >= 2) return "Increased Risk";
  if (counts.dual === 1) return "Standard Risk";
  return "Low Risk";
}

export function withSuggestion(assessment = {}) {
  const suggestedRisk = suggestFwdAmlRisk(assessment);
  const finalRisk = text(assessment.finalRisk);
  const riskOverridden = Boolean(assessment.riskOverridden) && finalRisk !== suggestedRisk;
  return {
    ...assessment,
    version: assessment.version || FWD_AML_ASSESSMENT_VERSION,
    suggestedRisk,
    riskOverridden,
    finalRisk: riskOverridden ? finalRisk : suggestedRisk,
  };
}

export function chooseFinalRisk(assessment, finalRisk) {
  const suggestedRisk = suggestFwdAmlRisk(assessment);
  return withSuggestion({
    ...assessment,
    finalRisk,
    riskOverridden: finalRisk !== suggestedRisk,
  });
}

export function createEmptyAssessment(client = {}, user = {}) {
  return withSuggestion({
    version: FWD_AML_ASSESSMENT_VERSION,
    assessorName: personName(user),
    advisorName: consultantLabel(client),
    assesseeName: clientDisplayName(client),
    assesseeCode: text(client.code),
    assesseeType: mapAssesseeType(client.type),
    reviewType: "",
    triggerReason: "",
    clientStatus: "",
    previousAssessmentDate: "",
    previousRiskLevel: "",
    lastEddDate: "",
    countriesConnected: "",
    highestCountryRisk: "",
    screenedParties: SCREENED_PARTIES.map((party) => ({
      id: party.id,
      checked: false,
      detail: "",
    })),
    trueHit: "",
    trueHitTypes: [],
    unacceptableFactors: {},
    businessTypes: [],
    businessTypeOther: "",
    factors15a: {},
    factors15b: {},
    monetary: {},
    dualFactors: {},
    remarks: "",
    riskOverridden: false,
    finalRisk: "",
  });
}

const answerAllowed = (value, row) =>
  value === "yes" || value === "no" || (row.na && value === "na");

const requireRows = (errors, key, rows, answers) => {
  const missing = rows.some((row) => !answerAllowed(answers?.[row.id], row));
  if (missing) errors[key] = `Answer every ${key} row`;
};

export function assessmentFieldErrors(assessment) {
  if (!assessment) return {};
  const route = assessmentRoute(assessment);
  const errors = {};
  const requireText = (key, value, message) => {
    if (!text(value)) errors[key] = message;
  };

  requireText("assessorName", assessment.assessorName, "Assessor name is required");
  requireText("advisorName", assessment.advisorName, "Advisor name is required");
  requireText("assesseeName", assessment.assesseeName, "Assessee name is required");
  requireText("assesseeType", assessment.assesseeType, "Assessee type is required");
  requireText("reviewType", assessment.reviewType, "Review type is required");
  if (assessment.reviewType === "trigger") {
    requireText("triggerReason", assessment.triggerReason, "Trigger reason is required");
  }
  requireText("clientStatus", assessment.clientStatus, "Client status is required");
  if (assessment.clientStatus === "existing") {
    requireText(
      "previousRiskLevel",
      assessment.previousRiskLevel,
      "Previous risk level is required"
    );
  }
  requireText(
    "countriesConnected",
    assessment.countriesConnected,
    "Connected countries are required"
  );
  requireText(
    "highestCountryRisk",
    assessment.highestCountryRisk,
    "Highest country risk is required"
  );

  if (route.sectionOpen["2"]) {
    const parties = assessment.screenedParties || [];
    if (!parties.some((party) => party.checked)) {
      errors.screenedParties = "Select at least one screened party";
    }
    const others = parties.find((party) => party.id === "others");
    if (others?.checked && !text(others.detail)) {
      errors.screenedPartyDetail = "Detail is required for other screened parties";
    }
    if (assessment.trueHit !== "yes" && assessment.trueHit !== "no") {
      errors.trueHit = "True hit is required";
    }
    if (route.showQuestion11 && !(assessment.trueHitTypes || []).length) {
      errors.trueHitTypes = "Select at least one true hit";
    }
  }

  if (route.sectionOpen["3"]) {
    requireRows(
      errors,
      "unacceptable-risk factor",
      UNACCEPTABLE_FACTORS.map((factor) => ({ ...factor, na: true })),
      assessment.unacceptableFactors
    );
  }

  if (route.sectionOpen["4"]) {
    if (!(assessment.businessTypes || []).length) {
      errors.businessTypes = "Select at least one business type";
    }
    if (
      (assessment.businessTypes || []).includes("others") &&
      !text(assessment.businessTypeOther)
    ) {
      errors.businessTypeOther = "Other business type is required";
    }
    if (route.show15a) {
      requireRows(errors, "question 15(a) factor", FACTORS_15A, assessment.factors15a);
    }
    if (route.show15b) {
      requireRows(errors, "question 15(b) factor", FACTORS_15B, assessment.factors15b);
    }
    if (route.show16) {
      requireRows(errors, "monetary threshold", MONETARY_ROWS, assessment.monetary);
    }
  }

  if (route.showSection5) {
    requireRows(errors, "dual risk factor", DUAL_FACTORS, assessment.dualFactors);
  }

  const synced = withSuggestion(assessment);
  if (!AML_RISK_LEVELS.includes(synced.finalRisk)) {
    errors.finalRisk = "Final risk rating is required";
  }
  if (synced.riskOverridden && !text(assessment.remarks)) {
    errors.remarks =
      "Remarks are required when the final rating differs from the suggestion";
  }

  return errors;
}

export function validateFwdAmlAssessment(assessment) {
  const first = Object.values(assessmentFieldErrors(assessment))[0];
  return first || null;
}

export function applyAssessmentRisk(row) {
  if (!row?.assessment) return row;
  const assessment = withSuggestion(row.assessment);
  return {
    ...row,
    assessment,
    riskLevel: assessment.finalRisk || row.riskLevel,
  };
}

export function latestOtherAml(rows, current = {}) {
  const currentId = current?._id ? String(current._id) : "";
  const others = (rows || []).filter(
    (row) => !currentId || String(row?._id || "") !== currentId
  );
  return (
    others
      .slice()
      .sort((left, right) => {
        const leftTime = Date.parse(left?.signDate) || 0;
        const rightTime = Date.parse(right?.signDate) || 0;
        return rightTime - leftTime;
      })[0] || null
  );
}

const hasEnteredDate = (value) => {
  if (value == null || value === "") return false;
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  return String(value).trim() !== "";
};

export function withLastAmlAssessmentDate(assessment = {}, rows, current = {}) {
  if (hasEnteredDate(assessment.previousAssessmentDate)) return assessment;
  const previous = latestOtherAml(rows, current);
  if (!previous?.signDate) return assessment;
  return { ...assessment, previousAssessmentDate: previous.signDate };
}

const countryLine = (country, ratings) => {
  const name = text(country);
  if (!name) return "";
  const screening = screenCountryRisk(ratings, name);
  if (!screening.label) return name;
  return [name, screening.label, screening.level].filter(Boolean).join(" · ");
};

const pepValue = (pep) => {
  if (!pep || typeof pep.isPep !== "boolean") return DASH;
  return [pep.isPep ? "Yes" : "No", text(pep.remark)].filter(Boolean).join(" · ");
};

const screeningItems = (client) => {
  const rows = nameScreeningRowsFromRecord(client?.dueDiligence?.nameScreening);
  if (!rows.length) return [{ label: "Name screening", value: DASH }];
  return rows.map((row) => {
    const hits = (row.outcome?.report?.hits || [])
      .map((hit) => [text(hit.matchListName), text(hit.decision)].filter(Boolean).join(": "))
      .filter(Boolean)
      .join("; ");
    return {
      label: text(row.label) || "Name screening",
      value: filled(
        [formatScreenedAt(row.outcome?.screenedAt), screenRecordStatusText(row), hits]
          .filter(Boolean)
          .join(" · ")
      ),
    };
  });
};

const relatedPartyValue = (client, lists) => {
  if (!lists) return DASH;
  const screening = screenRelatedParty(client, lists);
  if (screening.status === "MATCH") {
    return (
      screening.matches
        .map((match) =>
          [
            match.englishName,
            match.chineseName,
            match.companyName,
            match.companyRole,
            match.countryOfIncorporation,
          ]
            .map(text)
            .filter(Boolean)
            .join(" · ")
        )
        .filter(Boolean)
        .join("; ") || "Match"
    );
  }
  if (screening.status === "PASS") return "Pass";
  return "Not ready";
};

export function buildRelatedInformation(client = {}, context = {}) {
  const ratings = context.countryRatings;
  const resolvedCountries = resolveClientDueDiligenceCountries(client);
  const savedCountries = text(client?.dueDiligence?.country)
    .split(",")
    .map((country) => text(country))
    .filter(Boolean);
  const dueCountries = resolvedCountries.length
    ? resolvedCountries.map((item) => item.country)
    : savedCountries;
  const dueCountry = dueCountries
    .map((country) => countryLine(country, ratings))
    .filter(Boolean)
    .join("; ");
  const addressCountries = (client.addresses || [])
    .filter((address) => text(address?.country))
    .map((address) => `${addressTypeLabel(address) || "Address"}: ${text(address.country)}`);
  const countries = [
    text(client.residentialCountry) ? `Residential: ${text(client.residentialCountry)}` : "",
    ...addressCountries,
  ]
    .filter(Boolean)
    .join("; ");
  const previous = context.previousAml;
  const previousText = previous
    ? [formatDay(previous.signDate), formatDay(previous.expiryDate), text(previous.riskLevel)]
        .filter(Boolean)
        .join(" · ")
    : "";
  const counts = riskFactorCounts(context.assessment || {});
  const suggested = context.assessment ? suggestFwdAmlRisk(context.assessment) : "";
  const occupation = formatOccupationLabel(client.occupation);
  const parentName =
    client.parentCompany && typeof client.parentCompany === "object"
      ? text(client.parentCompany.name)
      : "";
  const investor = (() => {
    if (typeof client.professionalInvestor !== "boolean") return "";
    return [client.professionalInvestor ? "Yes" : "No", formatDay(client.piEffectiveDate)]
      .filter(Boolean)
      .join(" · ");
  })();

  return {
    section1: [
      { label: "Client", value: filled(clientDisplayName(client)) },
      { label: "Code", value: filled(client.code) },
      { label: "Type", value: filled(clientTypeLabel(client.type)) },
      { label: "Status", value: filled(client.status) },
      { label: "Advisor", value: filled(consultantLabel(client)) },
      { label: "Previous AML", value: filled(previousText) },
      { label: "Countries", value: filled(countries) },
      { label: "Due diligence country", value: filled(dueCountry) },
    ],
    section2: [...screeningItems(client), { label: "PEP", value: pepValue(client.pep) }],
    section3: [
      { label: "Due diligence country", value: filled(dueCountry) },
      { label: "Related party", value: relatedPartyValue(client, context.relatedPartyLists) },
      ...screeningItems(client),
      { label: "Occupation", value: filled(occupation) },
      { label: "Business nature", value: filled(client.bussinessNature) },
      { label: "Industry", value: filled(client.industry) },
      { label: "PEP", value: pepValue(client.pep) },
    ],
    section4: [
      { label: "Client type", value: filled(clientTypeLabel(client.type)) },
      { label: "Professional investor", value: filled(investor) },
      { label: "Nationality", value: filled(client.nationality) },
      { label: "Residential country", value: filled(client.residentialCountry) },
      {
        label: "Premium",
        value: "Premium totals are not on the client record.",
      },
    ],
    section5: [
      { label: "Occupation", value: filled(occupation) },
      { label: "Business nature", value: filled(client.bussinessNature) },
      { label: "Industry", value: filled(client.industry) },
      { label: "Due diligence country", value: filled(dueCountry) },
      { label: "Annex 1", value: ANNEX_1.join(" ") },
      { label: "Client type", value: filled(clientTypeLabel(client.type)) },
      { label: "Parent company", value: filled(parentName) },
      {
        label: "Product and channel",
        value: "Product and distribution channel are not on the client profile.",
      },
    ],
    section6: [
      { label: "Suggested rating", value: filled(suggested) },
      { label: "Yes in 15 or 16", value: String(counts.single) },
      { label: "Yes in 17 to 21", value: String(counts.dual) },
      { label: "Previous AML risk", value: filled(previous?.riskLevel) },
      { label: "Note 1", value: PAPER_NOTES.note1 },
      { label: "Note 2", value: PAPER_NOTES.note2 },
    ],
  };
}
